import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';

import { BigBuyBarDesktopGenderSizing } from 'productTemplate/components/buyBar/BigBuyBarDesktopGenderSizing';
import { BigBuyBarDesktopSwiperSection } from 'productTemplate/components/buyBar/BigBuyBarDesktopSwiperSection';
import { BigBuyBarDesktopTabs } from 'productTemplate/components/buyBar/BigBuyBarDesktopTabs';
import { BUY_BAR_STYLE } from 'productTemplate/enums/desktopBuyBar';
import { CloseX } from 'shared/icons/CloseX';
import { Loader } from 'shared/components/Loader';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useBigBuyBarDesktopView } from 'productTemplate/hooks/useBigBuyBarDesktopView';
import { useOffersContext } from 'productTemplate/hooks/useOffersContext';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

const TP = 'productTemplate.components.buyBar.BigBuyBarDesktop';

// TODO: css is a bit overused in this components
// use transform property instead for smoother animation or react-spring without having to instantiate too many properties
// only use flex in elements where it is needed (to build a grid) and let the children adapt to the given flow
// before repositioning them. This causes a lot of css override and nesting, making it a bit hard to debug.

export const BigBuyBarDesktop = ({ isFetchingBuyBarData }: { isFetchingBuyBarData: boolean }) => {
  const { query } = useRouter();
  const { isBuyBarExpanded, setIsBuyBarExpanded, isBuyBarSizeLarge } = useBigBuyBarDesktopView();
  const { isOfferable } = useOffersContext();
  const { availableSizes } = useStoreSelector((state) => state.pdp.buyBarVariants);
  const isUnavailable = !isOfferable && availableSizes.length === 0;
  const [allowExpansion, setAllowExpansion] = useState(true);
  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const expandBuyBar = () => setIsBuyBarExpanded(true);

  const closeBuyBar = () => setIsBuyBarExpanded(false);

  const handleMouseEnter = () => {
    if (isUnavailable) return;
    if (allowExpansion) {
      expandBuyBar();
    }
  };

  const handleClickCloseButton = () => {
    closeBuyBar();
    setAllowExpansion(false);
    setTimeout(() => setAllowExpansion(true), 250);
  };

  useEffect(() => {
    if (query?.expandedSize || query?.tab === 'used') {
      expandBuyBar();
    }
  }, [query]);

  useEffect(() => {
    if (isBuyBarExpanded) {
      sendTrackingEvent('MODAL_OPEN_VIEW', {
        modal_name: 'price_carousel',
        service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
      });
    }
  }, [isBuyBarExpanded, isConsumerSearchEnabled]);

  useEffect(() => {
    if (isUnavailable) {
      setIsBuyBarExpanded(false);
    }
  }, [isUnavailable, setIsBuyBarExpanded]);

  return (
    <BigBuyBarDesktop.Root data-qa="buy_bar_desktop">
      <BigBuyBarDesktop.Wrapper
        onMouseEnter={handleMouseEnter}
        $isBigBuyBarViewExpanded={isBuyBarExpanded}
        $isBuyBarSizeLarge={isBuyBarSizeLarge}
      >
        <BigBuyBarDesktop.OuterHeader $isBigBuyBarViewExpanded={isBuyBarExpanded}>
          <BigBuyBarDesktopGenderSizing />
        </BigBuyBarDesktop.OuterHeader>
        <BigBuyBarDesktop.Header $isBigBuyBarViewExpanded={isBuyBarExpanded}>
          <BigBuyBarDesktop.CloseButton
            aria-label={t(`${TP}.closeButton`, 'Close Buy Bar Panel')}
            type="button"
            onClick={handleClickCloseButton}
            data-qa="close_buy_bar_panel"
          >
            <CloseX />
          </BigBuyBarDesktop.CloseButton>
        </BigBuyBarDesktop.Header>
        {isFetchingBuyBarData ? (
          <Loader style={{ marginTop: '22px' }} />
        ) : (
          <>
            <BigBuyBarDesktopSwiperSection isBigBuyBarViewExpanded={isBuyBarExpanded} />
            <BigBuyBarDesktopTabs isBigBuyBarViewExpanded={isBuyBarExpanded} />
          </>
        )}
      </BigBuyBarDesktop.Wrapper>
    </BigBuyBarDesktop.Root>
  );
};

const expandedDefaultWrapperStyles = css`
  height: ${BUY_BAR_STYLE.EXPANDED_DEFAULT_HEIGHT}px;
  bottom: ${BUY_BAR_STYLE.EXPANDED_DEFAULT_HEIGHT - BUY_BAR_STYLE.COLLAPSED_HEIGHT}px;
  width: 648px;
  border-color: #000;
  border-width: 1px;
  border-style: solid;
  border-top-color: black;
  border-image: linear-gradient(to bottom, black, rgba(0, 0, 0, 0)) 1 100%;
  overflow: visible;
`;

const expandedLargeWrapperStyles = css`
  ${expandedDefaultWrapperStyles}
  height: ${BUY_BAR_STYLE.EXPANDED_LARGE_HEIGHT}px;
  bottom: ${BUY_BAR_STYLE.EXPANDED_LARGE_HEIGHT - BUY_BAR_STYLE.COLLAPSED_HEIGHT}px;
`;

BigBuyBarDesktop.Root = styled.div`
  ${variableFontRegular({ size: '11.5px', height: '14px' })}
  position: relative;
  height: 100%;
`;

// TODO: refactor to let this component responsively scale
// 490px is the maximum width the component must have to allow the name and the "fit fact want and arrow" to be
// seen at the lowest window width for desktop 769px
// 500px creates a cut off unless we reduce the margin/padding in between those elements
// Maybe a discussion for design team

BigBuyBarDesktop.Wrapper = styled.div<{
  $isBigBuyBarViewExpanded: boolean;
  $isBuyBarSizeLarge: boolean;
}>`
  position: relative;
  width: 500px;
  height: 75px;
  bottom: 0;
  margin: 0 auto;
  background-color: #fff;
  overflow: hidden;
  transition-property: height, bottom, width, border-color;
  transition-duration: 0.2s;
  transition-timing-function: ease-out;
  ${({ $isBigBuyBarViewExpanded, $isBuyBarSizeLarge }) =>
    $isBigBuyBarViewExpanded &&
    ($isBuyBarSizeLarge ? expandedLargeWrapperStyles : expandedDefaultWrapperStyles)}
`;

// Weird note: border top and negative margin below is a hack for a gradient border top bug.
BigBuyBarDesktop.OuterHeader = styled.div<{ $isBigBuyBarViewExpanded: boolean }>`
  width: 100%;
  display: flex;
  position: absolute;
  margin-top: -33px; // height of outer header + margin bottom 8px = 25 + 8 = 33;
  transition: opacity 0.2s ease-out;
  opacity: 0;
  visibility: hidden;
  ${({ $isBigBuyBarViewExpanded }) =>
    $isBigBuyBarViewExpanded &&
    css`
      opacity: 1;
      visibility: visible;
    `}

  button {
    ${variableFontRegular({ size: '12px' })}
  }
`;

BigBuyBarDesktop.Sizing = styled.div`
  width: fit-content;
  padding: 4px;
  border: 1px solid #000;
  margin: 0 auto;
  background-color: #fff;
`;

BigBuyBarDesktop.Header = styled.div<{ $isBigBuyBarViewExpanded: boolean }>`
  width: 100%;
  place-content: flex-end;
  transition: height 0.2s ease-out;
  height: 0;
  display: none;
  margin-top: 0;
  border-top: none;
  ${({ $isBigBuyBarViewExpanded }) =>
    $isBigBuyBarViewExpanded &&
    css`
      height: 24px;
      margin-top: -1px;
      border-top: 1px solid #000;
      display: flex;
    `}
`;

BigBuyBarDesktop.CloseButton = styled.button`
  width: 24px;
  margin-top: 7px;
  margin-right: 7px;
`;
